import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { BLOCK_REGISTRY } from '../registry';
import type { BlockNode, ShowForRule } from '../types';
import {
  createBlock,
  duplicateBlock as duplicateBlockInTree,
  findBlock,
  insertChild,
  removeBlock,
  reorderChildren,
  updateBlock,
} from '../utils';

/**
 * Content editor state manager.
 *
 * One store drives every app/page the editor manages. Each page's local draft
 * is persisted to localStorage (survives refresh and navigation), separate
 * from the database draft — publishing is the moment data leaves this store
 * for the API (wired in a later pass; today it just moves the local baseline).
 *
 * Split of responsibilities:
 * - Store (persisted): page drafts, published baselines, timestamps.
 * - Store (session): active page, selection, dialog/mode state, undo history.
 * - URL (nuqs, wired in hooks.ts): which page + which block is selected, so a
 *   refresh or shared link restores the exact editing position.
 */

const STORAGE_KEY = 'content-editor.v1';
const STORE_VERSION = 2;
/** Max undo steps kept per page. */
const HISTORY_LIMIT = 50;
/** Rapid edits to the same prop within this window collapse into one undo step. */
const COALESCE_MS = 1000;

export const pageKey = (appSlug: string, pageId: string) =>
  `${appSlug}:${pageId}`;

/** What the data layer hands the store when a page is opened (the fetched slot). */
export interface PageSeed {
  draft: BlockNode;
  /** The tree the server currently serves; undefined = never published. */
  published?: BlockNode;
  /** The slot's server `updatedAt` — sent back as the stale-save guard. */
  serverUpdatedAt?: string;
  publishedAt?: string;
}

/**
 * Content moves through three tiers:
 *   local draft (this store) → persisted draft (db) → published.
 * `savedDraft` is the local mirror of the db-draft tier; `published` mirrors
 * the published tier. Each has its own dirty flag.
 */
export interface EditorPage {
  key: string;
  appSlug: string;
  pageId: string;
  title: string;
  /** Local working copy — the only tree the editor mutates. */
  draft: BlockNode;
  /** Last draft persisted to the db ("Save draft" baseline). */
  savedDraft: BlockNode;
  /** Last-known published tree. Publish copies draft over it (API later). */
  published?: BlockNode;
  /** Draft differs from the published baseline (or was never published). */
  isDirty: boolean;
  /** Local draft differs from the db-draft baseline ("Save draft" enabled). */
  hasUnsavedChanges: boolean;
  updatedAt: string;
  /** When the draft was last persisted to the db. */
  draftSavedAt?: string;
  publishedAt?: string;
  /** The slot's server `updatedAt` — the optimistic-concurrency token. */
  serverUpdatedAt?: string;
}

interface PageHistory {
  past: BlockNode[];
  future: BlockNode[];
}

interface ContentEditorState {
  /* ------------------------- persisted ------------------------- */
  pages: Record<string, EditorPage>;

  /* ------------------------ session-only ----------------------- */
  activeKey?: string;
  selectedId?: string;
  /** Container the add-component dialog is adding into. */
  addTargetId?: string;
  /** Block staged for deletion, pending the confirm dialog. */
  pendingDeleteId?: string;
  /** Media prop the asset library dialog is editing. */
  assetPickerPropName?: string;
  /** The top-level "Add" component picker dialog is open. */
  regionAddOpen: boolean;
  /**
   * Block type pending placement: set when picked from the region add
   * dialog; while set, the preview shows insertion points. undefined = not
   * in placement mode.
   */
  insertType?: string;
  history: Record<string, PageHistory>;
  /** Coalescing marker for text-input edits (block.prop + timestamp). */
  lastEditKey?: string;
  lastEditAt?: number;

  /* -------------------------- lifecycle ------------------------ */
  /**
   * Activate a page. An existing local draft always wins (pick up where you
   * left off); otherwise the page is created from `seed` — today the mock
   * object, later the API response.
   */
  openPage: (args: {
    appSlug: string;
    pageId: string;
    title: string;
    seed: PageSeed;
  }) => void;
  /** Drop a page's local copy entirely; next open re-seeds it. */
  forgetPage: (key: string) => void;

  /* ------------------------- selection/UI ----------------------- */
  selectBlock: (id?: string) => void;
  setAddTarget: (id?: string) => void;
  setPendingDelete: (id?: string) => void;
  setAssetPickerProp: (name?: string) => void;
  /** Open/close the top-level "Add" component picker. */
  openRegionAdd: () => void;
  closeRegionAdd: () => void;
  /** Picker choice made → enter placement mode for `type`. */
  chooseInsertType: (type: string) => void;
  cancelInsert: () => void;

  /* ---------------------- draft mutations ----------------------- */
  updateBlockProp: (blockId: string, propName: string, value: unknown) => void;
  /** Sets/clears the block's layer name (node.name — identity metadata). */
  renameBlock: (blockId: string, name: string | undefined) => void;
  /**
   * Sets/clears the block's conditional-rendering rule (node.showFor).
   * undefined removes the field entirely — an unrestricted node stores no
   * rule. Enforcement is server-side; the editor only authors it.
   */
  updateShowFor: (blockId: string, showFor: ShowForRule | undefined) => void;
  toggleHidden: (blockId: string) => void;
  /** Deletes `pendingDeleteId` (the confirm dialog's confirm action). */
  confirmDelete: () => void;
  reorderBlock: (parentId: string, fromIndex: number, toIndex: number) => void;
  duplicateBlock: (blockId: string) => void;
  /** Adds `type` into `addTargetId` (the add-component dialog's pick). */
  addComponent: (type: string) => void;
  /** Placement-mode drop: inserts a new `insertType` block at `index`. */
  insertAt: (index: number) => void;

  /* --------------------------- history -------------------------- */
  undo: () => void;
  redo: () => void;

  /* --------------------- publish workflow ----------------------- */
  /**
   * Server confirmations: the view runs the API mutation, then reports the
   * outcome here so the local baselines move only on server success.
   * `savedTree` is the tree that was actually sent (the draft may have
   * changed during the request).
   */
  confirmDraftSaved: (savedTree: BlockNode, serverUpdatedAt: string) => void;
  confirmPublished: (
    publishedTree: BlockNode,
    serverUpdatedAt: string,
    publishedAt?: string,
  ) => void;
  /** Server discarded the draft (draft = published). Resets local state. */
  confirmDiscarded: (publishedTree: BlockNode, serverUpdatedAt: string) => void;
}

const now = () => new Date().toISOString();

const treesEqual = (a: BlockNode, b?: BlockNode) =>
  b !== undefined && JSON.stringify(a) === JSON.stringify(b);

const computeDirty = (draft: BlockNode, published?: BlockNode) =>
  !treesEqual(draft, published);

const EMPTY_HISTORY: PageHistory = { past: [], future: [] };

export const useContentEditorStore = create<ContentEditorState>()(
  persist(
    (set, get) => {
      /**
       * Apply a draft mutation to the active page: pushes the previous draft
       * onto the undo stack (unless coalescing), clears redo, recomputes
       * dirtiness. Returns a partial state update to spread into `set`.
       */
      const mutateDraft = (
        state: ContentEditorState,
        nextDraft: BlockNode,
        coalesceKey?: string,
      ): Partial<ContentEditorState> => {
        const key = state.activeKey;
        if (!key) return {};
        const page = state.pages[key];
        if (!page) return {};

        const nowMs = Date.now();
        const h = state.history[key] ?? EMPTY_HISTORY;
        const coalesce =
          coalesceKey !== undefined &&
          state.lastEditKey === coalesceKey &&
          nowMs - (state.lastEditAt ?? 0) < COALESCE_MS;
        const past = coalesce
          ? h.past
          : [...h.past.slice(-(HISTORY_LIMIT - 1)), page.draft];

        return {
          pages: {
            ...state.pages,
            [key]: {
              ...page,
              draft: nextDraft,
              isDirty: computeDirty(nextDraft, page.published),
              hasUnsavedChanges: !treesEqual(nextDraft, page.savedDraft),
              updatedAt: now(),
            },
          },
          history: { ...state.history, [key]: { past, future: [] } },
          lastEditKey: coalesceKey,
          lastEditAt: nowMs,
        };
      };

      const activePage = () => {
        const { activeKey, pages } = get();
        return activeKey ? pages[activeKey] : undefined;
      };

      return {
        pages: {},
        regionAddOpen: false,
        history: {},

        /* ------------------------- lifecycle ------------------------- */

        openPage: ({ appSlug, pageId, title, seed }) =>
          set((state) => {
            const key = pageKey(appSlug, pageId);
            const existing = state.pages[key];
            const samePage = state.activeKey === key;
            // The local draft wins only while it has unsaved changes;
            // otherwise adopt the fresh seed so server-side changes (another
            // editor, a refetch after save/publish) show up.
            const adoptSeed = !existing || !existing.hasUnsavedChanges;
            const page: EditorPage = adoptSeed
              ? {
                  key,
                  appSlug,
                  pageId,
                  title,
                  draft: seed.draft,
                  // The seed's draft IS the db draft — it starts as the
                  // "Save draft" baseline.
                  savedDraft: seed.draft,
                  published: seed.published,
                  isDirty: computeDirty(seed.draft, seed.published),
                  hasUnsavedChanges: false,
                  updatedAt: now(),
                  draftSavedAt: existing?.draftSavedAt,
                  publishedAt:
                    seed.publishedAt ?? (seed.published ? now() : undefined),
                  serverUpdatedAt: seed.serverUpdatedAt,
                }
              : existing;
            return {
              pages: { ...state.pages, [key]: page },
              activeKey: key,
              // Re-opening the SAME page (e.g. a refetch after save) keeps a
              // still-valid selection; switching pages clears transient UI —
              // selection is then restored from the URL by the sync hook.
              selectedId:
                samePage &&
                state.selectedId &&
                findBlock(page.draft, state.selectedId)
                  ? state.selectedId
                  : undefined,
              ...(samePage
                ? {}
                : {
                    addTargetId: undefined,
                    pendingDeleteId: undefined,
                    assetPickerPropName: undefined,
                    regionAddOpen: false,
                    insertType: undefined,
                    lastEditKey: undefined,
                  }),
            };
          }),

        forgetPage: (key) =>
          set((state) => {
            const pages = { ...state.pages };
            delete pages[key];
            const history = { ...state.history };
            delete history[key];
            return {
              pages,
              history,
              ...(state.activeKey === key
                ? { activeKey: undefined, selectedId: undefined }
                : {}),
            };
          }),

        /* ------------------------ selection/UI ----------------------- */

        selectBlock: (id) =>
          set(() => {
            // Guard against stale ids (e.g. a shared URL pointing at a block
            // that was deleted locally).
            const page = activePage();
            const valid = id && page && findBlock(page.draft, id);
            return { selectedId: valid ? id : undefined };
          }),

        setAddTarget: (id) => set({ addTargetId: id }),
        setPendingDelete: (id) => set({ pendingDeleteId: id }),
        setAssetPickerProp: (name) => set({ assetPickerPropName: name }),
        openRegionAdd: () => set({ regionAddOpen: true }),
        closeRegionAdd: () => set({ regionAddOpen: false }),
        chooseInsertType: (type) =>
          set({ regionAddOpen: false, insertType: type }),
        cancelInsert: () => set({ insertType: undefined }),

        /* --------------------- draft mutations ----------------------- */

        updateBlockProp: (blockId, propName, value) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = updateBlock(page.draft, blockId, (n) => ({
              ...n,
              props: { ...n.props, [propName]: value },
            }));
            // Coalesced per block+prop so typing a title is one undo step.
            return mutateDraft(state, next, `${blockId}.${propName}`);
          }),

        renameBlock: (blockId, name) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = updateBlock(page.draft, blockId, (n) => ({
              ...n,
              // undefined serializes away — a cleared name leaves no field.
              name: name?.trim() ? name : undefined,
            }));
            // Coalesced like prop edits so typing a name is one undo step.
            return mutateDraft(state, next, `${blockId}.name`);
          }),

        updateShowFor: (blockId, showFor) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = updateBlock(page.draft, blockId, (n) => {
              // undefined serializes away — an unrestricted node keeps no field.
              const { showFor: _cleared, ...rest } = n;
              return showFor ? { ...rest, showFor } : rest;
            });
            return mutateDraft(state, next);
          }),

        toggleHidden: (blockId) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = updateBlock(page.draft, blockId, (n) => ({
              ...n,
              hidden: !n.hidden,
            }));
            return mutateDraft(state, next);
          }),

        confirmDelete: () =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            const id = state.pendingDeleteId;
            if (!page || !id) return { pendingDeleteId: undefined };
            const deletedNode = findBlock(page.draft, id);
            const next = removeBlock(page.draft, id);
            // Clear selection if it was the deleted block or inside it.
            const selectionGone =
              state.selectedId !== undefined &&
              deletedNode !== undefined &&
              Boolean(findBlock(deletedNode, state.selectedId));
            return {
              ...mutateDraft(state, next),
              pendingDeleteId: undefined,
              ...(selectionGone ? { selectedId: undefined } : {}),
            };
          }),

        reorderBlock: (parentId, fromIndex, toIndex) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = reorderChildren(
              page.draft,
              parentId,
              fromIndex,
              toIndex,
            );
            return mutateDraft(state, next);
          }),

        duplicateBlock: (blockId) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            if (!page) return {};
            const next = duplicateBlockInTree(page.draft, blockId);
            return mutateDraft(state, next);
          }),

        addComponent: (type) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            const targetId = state.addTargetId;
            if (!page || !targetId) return { addTargetId: undefined };
            const block = createBlock(type, BLOCK_REGISTRY);
            const next = updateBlock(page.draft, targetId, (n) => ({
              ...n,
              children: [...n.children, block],
            }));
            return {
              ...mutateDraft(state, next),
              addTargetId: undefined,
              selectedId: block.id,
            };
          }),

        insertAt: (index) =>
          set((state) => {
            const page = state.activeKey
              ? state.pages[state.activeKey]
              : undefined;
            const type = state.insertType;
            if (!page || !type) return { insertType: undefined };
            const block = createBlock(type, BLOCK_REGISTRY);
            const next = insertChild(page.draft, page.draft.id, index, block);
            return {
              ...mutateDraft(state, next),
              insertType: undefined,
              selectedId: block.id,
            };
          }),

        /* --------------------------- history -------------------------- */

        undo: () =>
          set((state) => {
            const key = state.activeKey;
            const page = key ? state.pages[key] : undefined;
            const h = key ? state.history[key] : undefined;
            if (!key || !page || !h || h.past.length === 0) return {};
            const previous = h.past[h.past.length - 1];
            return {
              pages: {
                ...state.pages,
                [key]: {
                  ...page,
                  draft: previous,
                  isDirty: computeDirty(previous, page.published),
                  hasUnsavedChanges: !treesEqual(previous, page.savedDraft),
                  updatedAt: now(),
                },
              },
              history: {
                ...state.history,
                [key]: {
                  past: h.past.slice(0, -1),
                  future: [page.draft, ...h.future].slice(0, HISTORY_LIMIT),
                },
              },
              lastEditKey: undefined,
              selectedId:
                state.selectedId && findBlock(previous, state.selectedId)
                  ? state.selectedId
                  : undefined,
            };
          }),

        redo: () =>
          set((state) => {
            const key = state.activeKey;
            const page = key ? state.pages[key] : undefined;
            const h = key ? state.history[key] : undefined;
            if (!key || !page || !h || h.future.length === 0) return {};
            const [nextDraft, ...future] = h.future;
            return {
              pages: {
                ...state.pages,
                [key]: {
                  ...page,
                  draft: nextDraft,
                  isDirty: computeDirty(nextDraft, page.published),
                  hasUnsavedChanges: !treesEqual(nextDraft, page.savedDraft),
                  updatedAt: now(),
                },
              },
              history: {
                ...state.history,
                [key]: {
                  past: [...h.past, page.draft].slice(-HISTORY_LIMIT),
                  future,
                },
              },
              lastEditKey: undefined,
              selectedId:
                state.selectedId && findBlock(nextDraft, state.selectedId)
                  ? state.selectedId
                  : undefined,
            };
          }),

        /* --------------------- publish workflow ----------------------- */

        confirmDraftSaved: (savedTree, serverUpdatedAt) =>
          set((state) => {
            const key = state.activeKey;
            const page = key ? state.pages[key] : undefined;
            if (!key || !page) return {};
            return {
              pages: {
                ...state.pages,
                [key]: {
                  ...page,
                  savedDraft: savedTree,
                  // Compared against the CURRENT draft: edits made while the
                  // save was in flight stay marked unsaved.
                  hasUnsavedChanges: !treesEqual(page.draft, savedTree),
                  draftSavedAt: now(),
                  serverUpdatedAt,
                },
              },
            };
          }),

        confirmPublished: (publishedTree, serverUpdatedAt, publishedAt) =>
          set((state) => {
            const key = state.activeKey;
            const page = key ? state.pages[key] : undefined;
            if (!key || !page) return {};
            // Publishing persists the draft server-side too (published =
            // server draft), so the db-draft baseline moves with it.
            return {
              pages: {
                ...state.pages,
                [key]: {
                  ...page,
                  published: publishedTree,
                  savedDraft: publishedTree,
                  publishedAt: publishedAt ?? now(),
                  draftSavedAt: now(),
                  isDirty: computeDirty(page.draft, publishedTree),
                  hasUnsavedChanges: !treesEqual(page.draft, publishedTree),
                  serverUpdatedAt,
                },
              },
            };
          }),

        confirmDiscarded: (publishedTree, serverUpdatedAt) =>
          set((state) => {
            const key = state.activeKey;
            const page = key ? state.pages[key] : undefined;
            if (!key || !page) return {};
            return {
              pages: {
                ...state.pages,
                [key]: {
                  ...page,
                  draft: publishedTree,
                  savedDraft: publishedTree,
                  published: publishedTree,
                  hasUnsavedChanges: false,
                  isDirty: false,
                  updatedAt: now(),
                  serverUpdatedAt,
                },
              },
              history: { ...state.history, [key]: EMPTY_HISTORY },
              lastEditKey: undefined,
              selectedId:
                state.selectedId && findBlock(publishedTree, state.selectedId)
                  ? state.selectedId
                  : undefined,
            };
          }),
      };
    },
    {
      name: STORAGE_KEY,
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      // Only page content persists. Selection lives in the URL (nuqs) and
      // undo history is per-session by design.
      partialize: (state) => ({ pages: state.pages }),
      migrate: (persisted, version) => {
        const state = persisted as { pages?: Record<string, EditorPage> };
        // v1 → v2: pages predate the db-draft tier; treat what they had as
        // already saved (published if it exists, else the draft itself).
        if (version < 2 && state.pages) {
          for (const page of Object.values(state.pages)) {
            page.savedDraft = page.savedDraft ?? page.published ?? page.draft;
            page.hasUnsavedChanges = !treesEqual(page.draft, page.savedDraft);
          }
        }
        return state;
      },
    },
  ),
);

/** The currently active page, or undefined before openPage has run. */
export const useActivePage = () =>
  useContentEditorStore((s) =>
    s.activeKey ? s.pages[s.activeKey] : undefined,
  );
